import { useEffect } from 'react';
import { SEO_PAGES, NOINDEX_PREFIXES } from '../data/seoPages';

export const SITE = 'https://letechtools.com';

function setMeta(attr, key, value) {
  let m = document.querySelector(`meta[${attr}="${key}"]`);
  if (!m) {
    m = document.createElement('meta');
    m.setAttribute(attr, key);
    document.head.appendChild(m);
  }
  m.setAttribute('content', value);
}

// Lightweight per-route SEO for this single-page app. Updates the document
// title, meta description, canonical link, and the Open Graph / Twitter tags
// on mount so each route (e.g. the landing at "/" vs. the dashboard at
// "/tools") presents distinct metadata to crawlers and link unfurlers.
//
// `noindex: true` overrides index.html's default "index, follow" robots meta
// — for a soft-404 (this SPA's static host returns HTTP 200 for every path,
// so an unmatched route can't send a real 404 status) or an admin/internal
// page that has no business appearing in search results.
export function useSeo({ title, description, path, noindex = false }) {
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
    if (path != null) {
      const url = SITE + path;
      let c = document.querySelector('link[rel="canonical"]');
      if (!c) {
        c = document.createElement('link');
        c.setAttribute('rel', 'canonical');
        document.head.appendChild(c);
      }
      c.setAttribute('href', url);
      setMeta('property', 'og:url', url);
    }

    setMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large');
    // Restore the site-wide default when this page unmounts, so navigating
    // away from a noindex page doesn't leave the next page noindexed too.
    return () => {
      if (noindex) setMeta('name', 'robots', 'index, follow, max-image-preview:large');
    };
  }, [title, description, path, noindex]);
}

// Per-route SEO for every static route listed in data/seoPages.js, applied once
// for the whole app (see RouteSeo in App.js). Routes not listed there (guides,
// the 404 page) set their own with useSeo, which runs after this and wins.
export function useRouteSeo(pathname) {
  // A shared link (/canvass/<id>) takes its tool's title but stays out of search results.
  const shared = NOINDEX_PREFIXES.find(p => pathname.startsWith(p));
  const entry = SEO_PAGES[shared ? shared.slice(0, -1) : pathname];
  const page = entry && entry.canonical ? SEO_PAGES[entry.canonical] : entry;
  useSeo(page ? {
    title: page.title,
    description: page.description,
    path: shared ? pathname : (entry.canonical || pathname),
    noindex: !!(shared || page.noindex),
  } : {});
}

// Injects a JSON-LD structured-data block (removed on unmount / slug change) so
// each guide page can expose HowTo / BreadcrumbList schema for rich results.
export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    const elId = `ld-${id}`;
    let s = document.getElementById(elId);
    if (!s) {
      s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = elId;
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(data);
    return () => { const el = document.getElementById(elId); if (el) el.remove(); };
  }, [id, data]);
}
